// React
import { ReactElement, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
// Modules
import { MultiSelect } from 'react-multi-select-component';
import { toast } from 'react-toastify';
import classNames from 'classnames/bind';
// Design System
import { Button } from '@utmb/design-system/button/Button';
import { Font } from '@utmb/design-system/text/font';
// Features
import { getKeycloakToken } from '@utmb/features/sso/keycloak';
// Types
import { ColorTypesEnum } from '@utmb/types/colors';
import { UserQuestProps } from '@utmb/types/registration-form';
import { UserMemberUpdateStatusEnum } from '@utmb/types/enum/UserMemberUpdateStatusEnum';
import UserMemberUpdateStatusView from '@utmb/types/view/UserMemberUpdateStatusView';
import DashboardMemberPreferencesView from '@utmb/types/view/DashboardMemberPreferencesView';
// Context
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useUser } from '@utmb/contexts/AuthContext';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
// Datas
import {
    places as answersPlaces,
    distances as answersDistances,
    periods as answersPeriods,
    playgrounds as answersPlaygrounds,
    elevations as answersElevations,
} from './answers';
import { RegistrationConfirmation } from '@utmb/layouts/my-utmb/navigation';
// Styles
import styles from './user-quest.module.scss';

const cx = classNames.bind(styles);

export const UserQuest = (props: UserQuestProps): ReactElement => {
    const { isUpdating = false, onCancel } = props;

    const { dashboardMember, updateDashboardMemberPreferences } = useDashboard();
    const { refreshMe } = useUser();

    const { places, distances, periods, playgrounds, elevations } = dashboardMember?.preferences || ({} as DashboardMemberPreferencesView);

    const { t } = useTranslation();
    const router = useRouter();

    const { redirect_uri } = router.query;

    const convertToMultiSelectOptions = (items: any) => {
        if (!items) return [];
        return items.map((item: string) => ({
            label: t(`myutmb:myUtmb.yourQuest.answers.${item}`),
            value: item,
        }));
    };

    const [selectedPlaces, setSelectedPlaces] = useState(convertToMultiSelectOptions(places));
    const [selectedDistances, setSelectedDistances] = useState(convertToMultiSelectOptions(distances));
    const [selectedElevations, setSelectedElevations] = useState(convertToMultiSelectOptions(elevations));
    const [selectedPeriods, setSelectedPeriods] = useState(convertToMultiSelectOptions(periods));
    const [selectedPlaygrounds, setSelectedPlaygrounds] = useState(convertToMultiSelectOptions(playgrounds));

    const placesOptions = convertToMultiSelectOptions(answersPlaces);
    const distancesOptions = convertToMultiSelectOptions(answersDistances);
    const elevationsOptions = convertToMultiSelectOptions(answersElevations);
    const periodsOptions = convertToMultiSelectOptions(answersPeriods);
    const playgroundsOptions = convertToMultiSelectOptions(answersPlaygrounds);

    const multiselectTranslations = {
        selectSomeItems: t('common.select'),
        search: t('common.search'),
        selectAll: t('common.selectAll'),
        selectAllFiltered: t('common.selectAllFiltered'),
        allItemsAreSelected: t('common.allItemsAreSelected'),
    };

    const handleSubmit = async (event: any): Promise<boolean> => {
        event.preventDefault();

        const token = await getKeycloakToken();

        const body = {
            places: selectedPlaces.map((place: any) => place.value),
            distances: selectedDistances.map((distances: any) => distances.value),
            elevations: selectedElevations.map((elevation: any) => elevation.value),
            periods: selectedPeriods.map((period: any) => period.value),
            playgrounds: selectedPlaygrounds.map((playgrounds: any) => playgrounds.value),
        };

        const response = (await fetchTheAPI({
            isClient: true,
            path: '/users/dashboard/member/preferences',
            bearer: token,
            method: FetchMethodsEnum.PUT,
            body,
        })) as UserMemberUpdateStatusView;

        if (response.statusCode === UserMemberUpdateStatusEnum.OK) {
            toast.success(t('myutmb:myUtmb.myInformation.successMessage'));

            updateDashboardMemberPreferences(body as DashboardMemberPreferencesView);

            if (onCancel) {
                onCancel();
            } else {
                await refreshMe();
                if (redirect_uri) {
                    window.location.href = `${redirect_uri}`;
                } else router.push(RegistrationConfirmation.slug);
            }
        } else {
            toast.error(t('myutmb:myUtmb.myInformation.errorMessage'));
        }
        return false;
    };

    return (
        <div
            className={cx(styles.container, {
                container_is_updating: isUpdating,
            })}
        >
            {!isUpdating && (
                <Font.FuturaHeavy className={styles.title} desktop="56" as="h1">
                    {t('myutmb:myUtmb.yourQuest.title')}
                </Font.FuturaHeavy>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
                <ul className={styles.natural_form_list}>
                    <Font.FuturaBook className={styles.natural_form_item} mobile="16" desktop="18" as="li">
                        <span className={styles.label}>{t('myutmb:myUtmb.yourQuest.questions.places')}</span>
                        <MultiSelect
                            className={styles.multiselect}
                            labelledBy={t('common.select')}
                            value={selectedPlaces}
                            onChange={setSelectedPlaces}
                            options={placesOptions}
                            overrideStrings={multiselectTranslations}
                        />
                    </Font.FuturaBook>
                    <Font.FuturaBook className={styles.natural_form_item} mobile="16" desktop="18" as="li">
                        <span className={styles.label}>{t('myutmb:myUtmb.yourQuest.questions.distances')}</span>
                        <MultiSelect
                            className={styles.multiselect}
                            labelledBy={t('common.select')}
                            value={selectedDistances}
                            onChange={setSelectedDistances}
                            options={distancesOptions}
                            overrideStrings={multiselectTranslations}
                        />
                    </Font.FuturaBook>
                    <Font.FuturaBook className={styles.natural_form_item} mobile="16" desktop="18" as="li">
                        <span className={styles.label}>{t('myutmb:myUtmb.yourQuest.questions.denivelations')}</span>
                        <MultiSelect
                            className={styles.multiselect}
                            labelledBy={t('common.select')}
                            value={selectedElevations}
                            onChange={setSelectedElevations}
                            options={elevationsOptions}
                            overrideStrings={multiselectTranslations}
                        />
                    </Font.FuturaBook>
                    <Font.FuturaBook className={styles.natural_form_item} mobile="16" desktop="18" as="li">
                        <span className={styles.label}>{t('myutmb:myUtmb.yourQuest.questions.periods')}</span>
                        <MultiSelect
                            className={styles.multiselect}
                            labelledBy={t('common.select')}
                            value={selectedPeriods}
                            onChange={setSelectedPeriods}
                            options={periodsOptions}
                            overrideStrings={multiselectTranslations}
                        />
                    </Font.FuturaBook>
                    <Font.FuturaBook className={styles.natural_form_item} mobile="16" desktop="18" as="li">
                        <span className={styles.label}>{t('myutmb:myUtmb.yourQuest.questions.playgrounds')}</span>
                        <MultiSelect
                            className={styles.multiselect}
                            labelledBy={t('common.select')}
                            value={selectedPlaygrounds}
                            onChange={setSelectedPlaygrounds}
                            options={playgroundsOptions}
                            overrideStrings={multiselectTranslations}
                        />
                    </Font.FuturaBook>
                </ul>

                <div className={styles.form_submit_buttons}>
                    {onCancel && (
                        <Button.Default
                            color={getColorStyle(ColorTypesEnum.WHITE)}
                            bgcolor={getColorStyle(ColorTypesEnum.DEFAULT)}
                            type="submit"
                            label={t('myutmb:myUtmb.formButtons.cancel')}
                            onPress={onCancel}
                        />
                    )}
                    <Button.Default
                        color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                        bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                        type="submit"
                        label={t('myutmb:myUtmb.formButtons.submit')}
                    />
                </div>
            </form>
        </div>
    );
};
